// The strip above the FYI tab: what robot mail is, the one-click "Clear all", and
// the senders that look automated but aren't in the lane yet.
//
// See CODE_MAP.md "FYI lane for robot mail". Resolving goes through the same
// useTicketAction().runMany as the bulk bar (optimistic, 4 at a time, one toast),
// so the dispatcher stays the only place a ticket changes status (CLAUDE.md §5/§6).

import { useMemo, useState } from 'react';
import { Bot, CheckCheck, ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { useAuth } from '@/lib/auth-guard';
import { useAccess } from '@/lib/use-access';
import { useTicketAction } from '@/lib/use-ticket-action';
import { fetchAllSearchTickets } from '@/lib/search-ids';
import {
  clearAllCopy,
  planClearAll,
  readDismissed,
  visibleSuggestions,
  writeDismissed,
  type ClearPlan,
} from '@/lib/robot-mail';
import {
  fetchTicketSearchPage,
  useAutomatedSuggestions,
  useSetContactAutomated,
  type AutomatedSuggestion,
  type Ticket,
} from '@/lib/queries';
import type { SearchRpcArgs } from '@/lib/search-query';
import { toastFailure, toastSuccess } from '@/lib/toast';
import { cn } from '@/lib/utils';

function storage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export function FyiLaneBar({
  args,
  total,
  inboxId,
}: {
  /** The Dashboard's current search args (group 'fyi', plus any chips / search). */
  args: SearchRpcArgs;
  /** How many tickets the view matches (the tab badge). */
  total: number;
  inboxId: string;
}) {
  const access = useAccess();
  const { teamMember } = useAuth();
  const mySlackId = teamMember?.slack_user_id ?? null;
  const { runMany } = useTicketAction();
  const canResolve = access.canIn(inboxId, 'tickets.status');
  const canFlag = access.can('contacts.edit');

  const [loading, setLoading] = useState(false);
  const [pending, setPending] = useState<{ plan: ClearPlan; complete: boolean } | null>(null);

  // Read the WHOLE view (not just the rows on screen), then ask before resolving.
  const begin = async () => {
    setLoading(true);
    try {
      const all = await fetchAllSearchTickets<Ticket>((offset) => fetchTicketSearchPage(args, offset));
      const plan = planClearAll(all.tickets, mySlackId);
      if (plan.ids.length === 0) {
        toastSuccess('Nothing to clear', 'Everything in this view is already resolved.');
        return;
      }
      setPending({ plan, complete: all.complete });
    } catch (e) {
      toastFailure(e, "Couldn't load the list to clear");
    } finally {
      setLoading(false);
    }
  };

  const copy = pending ? clearAllCopy(pending.plan, pending.complete) : null;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border border-border/60 bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
        <Bot className="h-3.5 w-3.5 shrink-0" aria-hidden />
        <p className="min-w-0 flex-1">
          <span className="font-medium text-foreground/85">Automated mail.</span> Receipts, alerts and
          newsletters from senders marked as automated. They get no Slack card and no SLA clock, and
          nothing here needs a reply. Glance through, then clear it.
        </p>
        {canResolve && total > 0 ? (
          <Button size="sm" variant="outline" onClick={() => void begin()} disabled={loading}>
            {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCheck className="h-3.5 w-3.5" />}
            Clear all ({total})
          </Button>
        ) : null}
      </div>

      {canFlag ? <LooksAutomated inboxId={inboxId} /> : null}

      <ConfirmDialog
        open={!!pending}
        onOpenChange={(open) => {
          if (!open) setPending(null);
        }}
        title={copy?.title ?? ''}
        description={copy?.description ?? ''}
        confirmLabel={copy?.confirmLabel ?? 'Resolve'}
        onConfirm={() => {
          const p = pending;
          setPending(null);
          if (p) void runMany(p.plan.ids, 'resolve');
        }}
      />
    </div>
  );
}

/** Senders with many tickets and no reply that aren't flagged yet. A person decides
 *  each one: nothing is flagged on its own. */
function LooksAutomated({ inboxId }: { inboxId: string }) {
  const { teamMember } = useAuth();
  const q = useAutomatedSuggestions(inboxId);
  const setAutomated = useSetContactAutomated();
  const [open, setOpen] = useState(false);
  const [dismissed, setDismissed] = useState(() => readDismissed(storage()));
  const [busy, setBusy] = useState<string | null>(null);
  const list = useMemo(() => visibleSuggestions(q.data, dismissed), [q.data, dismissed]);

  if (list.length === 0) return null;

  const move = (s: AutomatedSuggestion) => {
    setBusy(s.email);
    setAutomated.mutate(
      { email: s.email, automated: true, editorEmail: teamMember?.email ?? null },
      {
        onSuccess: () =>
          toastSuccess('Moved to FYI', `Mail from ${s.name?.trim() || s.email} now waits here: no Slack card, no SLA clock.`),
        onError: (e) => toastFailure(e, "Couldn't move that sender"),
        onSettled: () => setBusy(null),
      },
    );
  };
  const notRobot = (s: AutomatedSuggestion) => {
    const next = new Set(dismissed).add(s.email.toLowerCase());
    setDismissed(next);
    writeDismissed(storage(), next);
  };

  return (
    <div className="rounded-lg border border-border/60 text-xs">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-left text-muted-foreground transition-colors hover:text-foreground"
      >
        {open ? <ChevronDown className="h-3.5 w-3.5 shrink-0" /> : <ChevronRight className="h-3.5 w-3.5 shrink-0" />}
        <span>
          <span className="font-medium tabular-nums text-foreground/85">{list.length}</span> more sender
          {list.length === 1 ? '' : 's'} look automated (many emails, none ever answered)
        </span>
      </button>
      {open ? (
        <ul className="max-h-72 divide-y divide-border/50 overflow-y-auto border-t border-border/60">
          {list.map((s) => (
            <li key={s.email} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2">
              <div className="min-w-0 flex-1">
                <div className="flex min-w-0 items-baseline gap-2">
                  <span className="truncate font-medium text-foreground/85">{s.name?.trim() || s.email}</span>
                  <span className="shrink-0 tabular-nums text-muted-foreground">{s.ticket_count} emails</span>
                </div>
                <div className="truncate text-muted-foreground">
                  {s.name?.trim() ? `${s.email} · ` : ''}
                  {s.last_subject ?? ''}
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                <Button size="sm" variant="outline" disabled={busy === s.email} onClick={() => move(s)}>
                  {busy === s.email ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Bot className="h-3.5 w-3.5" />}
                  Move to FYI
                </Button>
                <Button size="sm" variant="ghost" className={cn('text-muted-foreground')} onClick={() => notRobot(s)}>
                  Not a robot
                </Button>
              </div>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
